import type { AgentActionResponse } from '@monorepo/shared/schemas/agents'
import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

interface SignalCodeDialogProps {
  signal: AgentActionResponse
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SignalCodeDialog({
  signal,
  open,
  onOpenChange,
}: SignalCodeDialogProps) {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      toast.error(extractErrorMessage(err))
    }
  }

  const generateCode = () => {
    const aggregationType = signal.billable_signal?.aggregation_type
    const signalCode = signal.billable_signal?.signal_code || 'your_signal_code'
    const actionType = signal.action_type || 'task'

    const trackMethod =
      actionType === 'workflow'
        ? 'trackWorkflow'
        : actionType === 'outcome'
          ? 'trackOutcome'
          : 'trackTask'

    // Aggregation types that read a numeric value from attributes
    const needsBillingValue =
      aggregationType === 'sum' ||
      aggregationType === 'max' ||
      aggregationType === 'min' ||
      aggregationType === 'average'

    const hasCostSignals = signal.cost_signals && signal.cost_signals.length > 0

    const attributeLines: string[] = []

    if (needsBillingValue) {
      attributeLines.push(
        `    // Billing — enricher reads this for billing_quantity`
      )
      attributeLines.push(`    value: 100,`)
    }

    if (hasCostSignals) {
      if (attributeLines.length > 0) {
        attributeLines.push(``)
      }
      attributeLines.push(
        `    // Cost — enricher reads cost_usd for margin tracking`
      )
      attributeLines.push(`    cost_usd: 0.05,`)
    }

    const attributesBlock =
      attributeLines.length > 0
        ? `\n  attributes: {\n${attributeLines.join('\n')}\n  },`
        : ''

    return `import { Coomon } from '@coomon/sdk'

const coomon = new Coomon('your-api-key')

// Replace 'cust_...' with your customer's ID
coomon.${trackMethod}({
  customerId: 'cust_...',
  signalCode: '${signalCode}',${attributesBlock}
  metadata: { /* optional non-billing context */ },
})

await coomon.shutdown() // call before process exit`
  }

  const code = generateCode()
  const hasCostSignals = signal.cost_signals && signal.cost_signals.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-3'>
            Code Integration
            <Badge variant='outline' className='text-xs'>
              {signal.action_type || 'task'}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Copy and paste this code to integrate the "
            {signal.action_name || 'action'}" action into your application.
          </DialogDescription>
        </DialogHeader>

        {/* Single combined code block */}
        <div>
          <div className='flex items-center justify-between mb-2'>
            <h4 className='text-sm font-medium'>Implementation</h4>
            <Button
              variant='outline'
              size='sm'
              onClick={() => copyToClipboard(code)}
              className='h-7 px-2 text-xs'
            >
              {copied ? (
                <>
                  <Check className='w-3 h-3 mr-1' />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className='w-3 h-3 mr-1' />
                  Copy
                </>
              )}
            </Button>
          </div>
          <div className='bg-slate-50 dark:bg-slate-900 rounded-lg p-4 font-mono text-sm overflow-x-auto'>
            <pre className='text-slate-800 dark:text-slate-200 whitespace-pre-wrap'>
              {code}
            </pre>
          </div>
          <p className='text-xs text-muted-foreground mt-2'>
            One event drives both billing and cost — the enricher derives each
            from the same payload.
          </p>
        </div>

        {/* Signal Configuration */}
        <div className='border-t pt-4 mt-2'>
          <h4 className='text-sm font-medium mb-3'>Signal Configuration</h4>
          <div className='grid grid-cols-2 gap-4 text-sm'>
            <div>
              <span className='text-muted-foreground'>Billable Signal:</span>
              <div className='font-mono text-xs bg-muted px-2 py-1 rounded mt-1'>
                {signal.billable_signal?.signal_code || '—'}
              </div>
            </div>
            <div>
              <span className='text-muted-foreground'>Aggregation:</span>
              <div className='font-medium mt-1'>
                {signal.billable_signal?.aggregation_type || 'count'}
              </div>
            </div>
          </div>

          {hasCostSignals && (
            <div className='mt-4'>
              <span className='text-muted-foreground text-sm'>
                Cost Signals (auto-calculated from same event):
              </span>
              <div className='mt-1 space-y-1'>
                {signal.cost_signals?.map((cs, i) => (
                  <div
                    key={cs.signal_id || i}
                    className='flex items-center justify-between text-xs bg-muted px-2 py-1 rounded'
                  >
                    <span className='font-mono'>
                      {cs.signal_code || cs.signal_name}
                    </span>
                    {cs.cost_config?.fixed_cost_cents ? (
                      <span className='text-muted-foreground'>
                        ${(cs.cost_config.fixed_cost_cents / 100).toFixed(4)} /
                        event (fixed)
                      </span>
                    ) : (
                      <span className='text-muted-foreground'>
                        reads attributes.cost_usd
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Usage Notes */}
        <div className='border-t pt-4 mt-4'>
          <h4 className='text-sm font-medium mb-2'>Usage Notes</h4>
          <ul className='text-sm text-muted-foreground space-y-1 list-disc list-inside'>
            <li>Replace 'your-api-key' with your actual Coomon API key</li>
            <li>Replace 'cust_...' with the ID of the customer being billed</li>
            {signal.billable_signal?.aggregation_type === 'sum' && (
              <li>
                Sum aggregation: set <code>attributes.value</code> to the
                numeric quantity your signal bills on
              </li>
            )}
            {signal.billable_signal?.aggregation_type === 'unique_count' && (
              <li>
                Unique-count aggregation: set <code>attributes.value</code> to
                the distinct identifier to count
              </li>
            )}
            {hasCostSignals && (
              <li>
                Set <code>attributes.cost_usd</code> to your actual provider
                cost — used for margin tracking, not charged to the customer
              </li>
            )}
            <li>
              Use <code>metadata</code> for non-billing context (request IDs,
              session info) — stored but never used for billing
            </li>
            <li>
              Call <code>await coomon.shutdown()</code> before your process
              exits to flush any queued events
            </li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  )
}
